/**
 * O STRIPE.JS, carregado só quando é preciso.
 *
 * ── POR QUE NÃO É UM `import` ─────────────────────────────────────────────
 *
 * Porque ele quase nunca é preciso. O 3-D Secure só aparece quando o banco do
 * cliente o exige numa compra de pacote — uma fração de uma fração das sessões —
 * e um `<script>` da Stripe no `index.html` custaria o download a TODA gente que
 * abre o painel, incluindo quem nunca vai à tela de cobrança. Este módulo baixa
 * a biblioteca na primeira vez que ela faz falta e nunca uma segunda.
 *
 * ── SEM CSP PARA MEXER ────────────────────────────────────────────────────
 *
 * Conferido em 08/09/2026 contra `https://v3.benvox.com/`: a resposta não traz
 * `Content-Security-Policy` nenhum — nem do Caddy, nem do `index.html`, nem da
 * API (o mesmo facto que `compile-sfc.spec.ts` já registava desde 22/08). Não há
 * lista de hosts a liberar. No dia em que houver, o host é `js.stripe.com` e
 * `script-src` é a diretiva.
 *
 * ── A PROMESSA É MEMORIZADA, E NÃO O RESULTADO ────────────────────────────
 *
 * Duas compras que caem em 3-D Secure ao mesmo tempo pediriam dois downloads se
 * o cache guardasse só o objeto já pronto. Guardando a PROMESSA, a segunda
 * espera pela primeira. E um carregamento que falha limpa o cache: uma rede que
 * caiu por um segundo não pode condenar a aba inteira a nunca mais tentar.
 */

const SRC = "https://js.stripe.com/v3";

/**
 * O mínimo do Stripe.js que este produto usa. Escrito, e não importado de um
 * pacote: são dois campos e uma função, e `@stripe/stripe-js` traria um pacote
 * inteiro para os declarar.
 *
 * `confirmCardPayment` e não `handleCardAction`, e a diferença é a que faz o
 * fluxo funcionar: uma cobrança OFF-SESSION que o banco mandou autenticar deixa
 * o PaymentIntent em `requires_payment_method` — a Stripe desistiu dele sem
 * ninguém ao teclado —, e `handleCardAction` só sabe retomar um
 * `requires_action`. O que traz a pessoa de volta para dentro do pagamento é
 * confirmar de novo, ON-session, com o mesmo cartão. É aí que o desafio abre.
 */
export interface StripeJs {
	confirmCardPayment(
		clientSecret: string,
		options?: { payment_method?: string },
	): Promise<{
		error?: { code?: string; message?: string };
		paymentIntent?: { id: string; status: string };
	}>;
}

type StripeFactory = (key: string) => StripeJs;

declare global {
	interface Window {
		Stripe?: StripeFactory;
	}
}

let loading: Promise<StripeFactory> | null = null;

function load(): Promise<StripeFactory> {
	if (window.Stripe) return Promise.resolve(window.Stripe);
	if (loading) return loading;

	loading = new Promise<StripeFactory>((resolve, reject) => {
		// Um `<script>` já na página — de um carregamento anterior desta mesma aba,
		// ou de qualquer outra coisa que o tenha posto lá — é reaproveitado: dois
		// `Stripe.js` na mesma página é um aviso da própria biblioteca.
		const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SRC}"]`);
		const tag = existing ?? document.createElement("script");
		tag.addEventListener("load", () => {
			const factory = window.Stripe;
			if (factory) resolve(factory);
			else reject(new Error("stripe.js loaded without exposing Stripe"));
		});
		tag.addEventListener("error", () => reject(new Error("stripe.js failed to load")));
		if (!existing) {
			tag.src = SRC;
			tag.async = true;
			document.head.appendChild(tag);
		}
	});

	// Uma rede que caiu por um segundo não pode condenar a aba a nunca mais tentar.
	loading.catch(() => {
		loading = null;
	});
	return loading;
}

/**
 * A biblioteca, pronta e com a chave desta instalação.
 *
 * A chave vem do servidor (`GET /v3/billing/config`) e não de uma variável de
 * build: uma instalação pode apontar para outra conta Stripe sem recompilar o
 * painel, e uma chave escrita no bundle é uma chave que fica errada em silêncio.
 */
export async function stripeWith(publishableKey: string): Promise<StripeJs> {
	if (!publishableKey) throw new Error("no publishable key");
	const factory = await load();
	return factory(publishableKey);
}

/** Só para os testes: esquece o carregamento em curso entre um caso e outro. */
export function resetStripeJs(): void {
	loading = null;
}
